/**
 * AnwaltsVollmacht — A4-Vollmacht für die anwaltliche Vertretung des
 * Geschädigten. Layout 1:1 im Stil der AbtretungsErklaerung.
 *
 * • Anwalt wird aus den zentralen Stammdaten (GET /lawyers) ausgewählt.
 * • Unterschriftsfeld für den Geschädigten (SignaturePad) — gespeichert
 *   als data-URL in case.anwalt_vollmacht.
 * • Persistenz: PATCH /cases/{id} { anwalt_vollmacht: {...} }
 */
import React, { useEffect, useRef, useState } from "react";
import SignaturePad from "react-signature-canvas";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, PenLine, RotateCcw, Scale, Trash2, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const COLORS = {
  ink: "#0B1320",
  body: "#1f2937",
  mute: "#65718A",
  accent: "var(--brand-1)",
  light: "#F1F5F9",
  border: "#E5E7EB",
  white: "#FFFFFF",
};
const FONT_STACK = "'Helvetica', 'Arial', sans-serif";
const LOGO_URL = "/easy-gutachter-logo.png";
const dash = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const VOLLMACHT_CLAUSES = [
  {
    num: 1,
    title: "Umfang der Vollmacht",
    text: "Die Vollmacht umfasst die außergerichtliche und gerichtliche Vertretung in der oben bezeichneten Schadensangelegenheit, insbesondere die Geltendmachung sämtlicher Schadensersatzansprüche (Reparaturkosten, Wertminderung, Nutzungsausfall, Mietwagenkosten, Sachverständigenkosten, Schmerzensgeld und sonstige Nebenforderungen) gegenüber dem Schädiger, dem Halter und dessen Haftpflichtversicherung.",
  },
  {
    num: 2,
    title: "Empfangs- und Geldempfangsvollmacht",
    text: "Die Vollmacht schließt die Befugnis ein, Zustellungen entgegenzunehmen sowie Zahlungen und Wertsachen in Empfang zu nehmen, insbesondere die Entschädigungsleistungen der gegnerischen Versicherung.",
  },
  {
    num: 3,
    title: "Untervollmacht",
    text: "Die bevollmächtigte Kanzlei ist berechtigt, Untervollmacht zu erteilen sowie die Vollmacht ganz oder teilweise auf andere Rechtsanwältinnen und Rechtsanwälte zu übertragen.",
  },
  {
    num: 4,
    title: "Datenschutz",
    text: "Ich willige ein, dass meine personenbezogenen Daten sowie die Daten des Schadenfalls (inkl. Gutachten und Lichtbilder) zur Durchsetzung meiner Ansprüche an die bevollmächtigte Kanzlei übermittelt und dort verarbeitet werden.",
  },
];

function SectionEyebrow({ children }) {
  return (
    <div style={{
      fontSize: "8pt", fontWeight: 700, color: COLORS.accent,
      letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "1.5mm",
    }}>{children}</div>
  );
}

function KVList({ rows }) {
  const visible = rows.filter((r) => r && r[1] !== null && r[1] !== undefined && r[1] !== "");
  if (visible.length === 0) return null;
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "10pt", marginTop: "2mm" }}>
      <tbody>
        {visible.map(([label, value], i) => (
          <tr key={i}>
            <td style={{
              padding: "3mm 4mm", color: COLORS.body, width: "55mm",
              verticalAlign: "top", background: COLORS.light,
              fontWeight: 700, borderBottom: `1pt solid ${COLORS.white}`,
            }}>{label}</td>
            <td style={{
              padding: "3mm 4mm", color: COLORS.ink, verticalAlign: "top",
              whiteSpace: "pre-line", background: COLORS.white,
            }}>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
}

export default function AnwaltsVollmacht({ c, user, readOnly = false, onSaved }) {
  const [lawyers, setLawyers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sigSaving, setSigSaving] = useState(false);
  const [sigEmpty, setSigEmpty] = useState(true);
  const sigRef = useRef(null);

  const vollmacht = c.anwalt_vollmacht || {};
  const selectedLawyerId = vollmacht.lawyer_id || "";
  const lawyer = vollmacht.lawyer || {};

  useEffect(() => {
    api.get("/lawyers").then(({ data }) => setLawyers(data.lawyers || [])).catch(() => {});
  }, []);

  const cust = c.customer || {};
  const ins = c.insurance || {};
  const fullName = [cust.firstname, cust.name].filter(Boolean).join(" ");
  const fullAddress = [cust.address, [cust.postal_code, cust.city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const accidentDate = fmtDate(c.accident_date || c.order_date || ins.claim_date);
  const today = new Date().toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
  const signedDate = vollmacht.signed_at ? fmtDate(vollmacht.signed_at) : today;

  const patchVollmacht = async (patch) => {
    const next = { ...vollmacht, ...patch };
    setSaving(true);
    try {
      await api.patch(`/cases/${c.id}`, { anwalt_vollmacht: next });
      onSaved?.(next);
    } catch (e) {
      toast.error(apiError(e, "Speichern fehlgeschlagen"));
    } finally {
      setSaving(false);
    }
  };

  const selectLawyer = async (lawyerId) => {
    const lw = lawyers.find((l) => l.id === lawyerId);
    if (!lw) return;
    await patchVollmacht({
      lawyer_id: lw.id,
      lawyer: {
        name: lw.name, firm: lw.firm, address_line: lw.address_line,
        postal_code: lw.postal_code, city: lw.city, phone: lw.phone, email: lw.email,
      },
      assigned_at: new Date().toISOString(),
    });
    toast.success(`Anwalt „${lw.firm || lw.name}" zugewiesen`);
  };

  const saveSignature = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error("Bitte zuerst im Feld unterschreiben.");
      return;
    }
    setSigSaving(true);
    try {
      await patchVollmacht({
        signature_data_url: sigRef.current.toDataURL("image/png"),
        signed_at: new Date().toISOString(),
        signer_name: fullName,
      });
      toast.success("Vollmacht unterschrieben");
    } finally {
      setSigSaving(false);
    }
  };

  const clearSignature = async () => {
    sigRef.current?.clear();
    setSigEmpty(true);
    if (vollmacht.signature_data_url) {
      await patchVollmacht({ signature_data_url: "", signed_at: null, signer_name: "" });
      toast.message("Unterschrift entfernt");
    }
  };

  // Responsive A4-Scaling (wie AbtretungsErklaerung)
  const A4_WIDTH_PX = 794;
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const compute = () => {
      const w = el.offsetWidth || 0;
      setScale(w > 0 && w < A4_WIDTH_PX ? w / A4_WIDTH_PX : 1);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const owner = c.owner || user || {};
  const caseRef = c.case_reference || c.case_number || c.id?.slice(0, 8);
  const lawyerLabel = selectedLawyerId
    ? ([lawyer.firm, lawyer.name].filter(Boolean).join(" · ") || "Anwalt")
    : null;

  return (
    <div className="space-y-4" data-testid="anwalts-vollmacht">
      {/* ── Anwalt-Auswahl (Stammdaten) ── */}
      <div className="rounded-2xl border border-zinc-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-2">
          <Scale className="h-4 w-4 text-cyan-700" />
          <span className="text-sm font-semibold text-zinc-900">Anwalt / Kanzlei auswählen</span>
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />}
          {selectedLawyerId && (
            <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100 rounded px-1.5 py-0.5 inline-flex items-center gap-1" data-testid="vollmacht-lawyer-badge">
              <CheckCircle2 className="h-3 w-3" /> zugewiesen
            </span>
          )}
        </div>
        {lawyers.length === 0 ? (
          <div className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2.5" data-testid="vollmacht-no-lawyers">
            Noch keine Anwälte hinterlegt. Die Kanzleien werden zentral in der{" "}
            <strong>Buchhaltung → Anwälte</strong> als Stammdaten gepflegt.
          </div>
        ) : (
          <Select value={selectedLawyerId} onValueChange={selectLawyer} disabled={readOnly || saving}>
            <SelectTrigger data-testid="vollmacht-lawyer-select">
              <SelectValue placeholder="Kanzlei wählen…" />
            </SelectTrigger>
            <SelectContent>
              {lawyers.map((l) => (
                <SelectItem key={l.id} value={l.id} data-testid={`vollmacht-lawyer-option-${l.id}`}>
                  {[l.firm, l.name].filter(Boolean).join(" · ")}{l.city ? ` (${l.city})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* ── A4-Dokument ── */}
      <div
        ref={containerRef}
        className="w-full"
        data-testid="vollmacht-paper"
        style={{ height: scale < 1 ? `${307 * scale}mm` : "auto", overflow: "hidden" }}
      >
        <div style={{
          width: "210mm", margin: "0 auto",
          transformOrigin: "top left",
          transform: scale < 1 ? `scale(${scale})` : "none",
        }}>
          <div
            className="doc-a4 mx-auto bg-white shadow-md relative"
            style={{
              width: "210mm", minHeight: "297mm",
              padding: "22mm 20mm 24mm 20mm",
              fontFamily: FONT_STACK, fontSize: "10pt", lineHeight: 1.5,
              color: COLORS.body, marginBottom: "10mm",
            }}
          >
            {/* Header */}
            <div style={{
              position: "absolute", top: "10mm", left: "20mm", right: "20mm",
              display: "flex", justifyContent: "space-between", alignItems: "flex-start",
              paddingBottom: "3mm", borderBottom: `0.5pt solid ${COLORS.border}`,
              fontSize: "8.5pt", color: COLORS.mute,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <img src={LOGO_URL} alt="Easy Gutachter" style={{ height: "8mm", width: "auto" }} />
                <span style={{ fontWeight: 700, color: COLORS.ink, fontSize: "9.5pt" }}>Easy Gutachter</span>
              </div>
              <div style={{ fontSize: "8pt", textAlign: "right" }}>
                {caseRef && <span>AZ: <span style={{ color: COLORS.ink, fontWeight: 600 }}>{caseRef}</span></span>}
              </div>
            </div>

            <div style={{ paddingTop: "10mm" }}>
              <div style={{ marginTop: "8mm", marginBottom: "10mm" }}>
                <SectionEyebrow>Vollmacht · Rechtsanwaltliche Vertretung</SectionEyebrow>
                <h1 style={{
                  fontSize: "28pt", fontWeight: 700, color: COLORS.ink,
                  margin: 0, lineHeight: 1.15, letterSpacing: "-0.01em",
                }}>Vollmacht</h1>
                <div style={{ marginTop: "3mm", fontSize: "9pt", color: COLORS.mute }}>
                  {[cust.city, today].filter(Boolean).join(", ")}
                </div>
              </div>

              <div style={{ marginTop: "6mm" }}>
                <SectionEyebrow>Vollmachtgeber · Geschädigter</SectionEyebrow>
                <KVList rows={[
                  ["Name", fullName],
                  ["Anschrift", fullAddress],
                ]} />
              </div>

              <div style={{ marginTop: "6mm" }}>
                <SectionEyebrow>Bevollmächtigte Kanzlei</SectionEyebrow>
                {selectedLawyerId ? (
                  <KVList rows={[
                    ["Kanzlei", lawyer.firm || lawyer.name],
                    ["Ansprechpartner", lawyer.firm ? lawyer.name : ""],
                    ["Anschrift", [lawyer.address_line, [lawyer.postal_code, lawyer.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")],
                    ["Kontakt", [lawyer.phone, lawyer.email].filter(Boolean).join(" · ")],
                  ]} />
                ) : (
                  <div style={{
                    padding: "4mm", background: COLORS.light, color: COLORS.mute,
                    fontStyle: "italic", fontSize: "9.5pt",
                  }} data-testid="vollmacht-lawyer-placeholder">
                    Bitte oben eine Kanzlei auswählen.
                  </div>
                )}
              </div>

              <div style={{ marginTop: "6mm" }}>
                <SectionEyebrow>Schadensangelegenheit</SectionEyebrow>
                <KVList rows={[
                  ["Verkehrsunfall vom", accidentDate],
                  ["Gegnerische Versicherung", ins.opponent_insurer || ins.insurer_name || ins.insurer || cust.opponent_insurance],
                  ["Schadennummer", ins.claim_number],
                  ["Sachverständigenbüro", owner.company || owner.name],
                ]} />
              </div>

              {/* Klauseln */}
              <div style={{ marginTop: "8mm" }}>
                {VOLLMACHT_CLAUSES.map((cl) => (
                  <div key={cl.num} style={{ marginBottom: "5mm" }} data-testid={`vollmacht-clause-${cl.num}`}>
                    <div style={{
                      fontSize: "10pt", fontWeight: 700, color: COLORS.ink,
                      marginBottom: "1.5mm", display: "flex", alignItems: "baseline", gap: "6px",
                    }}>
                      <span style={{ color: COLORS.accent, minWidth: "12px" }}>{cl.num}.</span>
                      <span>{cl.title}</span>
                    </div>
                    <p style={{
                      color: COLORS.body, lineHeight: 1.55, margin: 0,
                      paddingLeft: "18px", textAlign: "justify",
                    }}>{cl.text}</p>
                  </div>
                ))}
              </div>

              {/* Ort/Datum + Unterschrift */}
              <div style={{
                marginTop: "14mm",
                display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12mm",
              }}>
                <div>
                  <SectionEyebrow>Ort, Datum</SectionEyebrow>
                  <div style={{
                    borderBottom: `1pt solid ${COLORS.ink}`,
                    paddingBottom: "8mm", color: COLORS.ink, fontWeight: 500,
                  }}>
                    {[cust.city, signedDate].filter(Boolean).join(", ") || "—"}
                  </div>
                </div>
                <div>
                  <SectionEyebrow>Unterschrift Vollmachtgeber</SectionEyebrow>
                  <div style={{
                    borderBottom: `1pt solid ${COLORS.ink}`,
                    paddingBottom: vollmacht.signature_data_url ? "1mm" : "8mm",
                    color: COLORS.ink, fontWeight: 500,
                    minHeight: "12mm", display: "flex", alignItems: "flex-end",
                  }}>
                    {vollmacht.signature_data_url ? (
                      <img
                        src={vollmacht.signature_data_url}
                        alt="Unterschrift Vollmachtgeber"
                        style={{ maxHeight: "16mm", maxWidth: "100%", objectFit: "contain", objectPosition: "left bottom" }}
                        data-testid="vollmacht-signature-img"
                      />
                    ) : (
                      <span style={{ color: COLORS.mute, fontStyle: "italic" }}>{fullName || "—"}</span>
                    )}
                  </div>
                  <div style={{ fontSize: "7.5pt", color: COLORS.mute, marginTop: "1.5mm" }}>
                    {dash(fullName)}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              position: "absolute", bottom: "12mm", left: "20mm", right: "20mm",
              borderTop: `0.5pt solid ${COLORS.border}`, paddingTop: "3mm",
              fontSize: "7.5pt", color: COLORS.mute,
              display: "flex", justifyContent: "space-between",
            }}>
              <div style={{ fontWeight: 700, color: COLORS.ink }}>
                {dash(owner.name)}{owner.company ? ` · ${owner.company}` : ""}
              </div>
              <div style={{ color: COLORS.ink, fontWeight: 600 }}>1 / 1</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Unterschriften-Pad ── */}
      {!readOnly && (
        <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid="vollmacht-sign-block">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-500">
              Unterschrift Vollmachtgeber {lawyerLabel ? `· ${lawyerLabel}` : ""}
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={clearSignature} data-testid="vollmacht-sig-clear">
              {vollmacht.signature_data_url ? <Trash2 className="h-4 w-4 mr-1" /> : <RotateCcw className="h-4 w-4 mr-1" />}
              Löschen
            </Button>
          </div>
          {vollmacht.signature_data_url ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4 flex items-center gap-4" data-testid="vollmacht-signed-state">
              <img src={vollmacht.signature_data_url} alt="Unterschrift" className="h-16 object-contain" />
              <div className="text-xs text-emerald-800">
                <div className="font-semibold flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Unterschrieben
                </div>
                <div className="text-zinc-500 mt-0.5">
                  {vollmacht.signer_name} · {fmtDate(vollmacht.signed_at)}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="rounded-xl border-2 border-dashed border-zinc-300 bg-zinc-50 overflow-hidden">
                <SignaturePad
                  ref={sigRef}
                  penColor="#0b1320"
                  onEnd={() => setSigEmpty(sigRef.current?.isEmpty() ?? true)}
                  canvasProps={{
                    className: "w-full h-40 touch-none",
                    style: { touchAction: "none", display: "block", width: "100%", height: "10rem" },
                    "data-testid": "vollmacht-signature-canvas",
                  }}
                />
              </div>
              <Button
                onClick={saveSignature}
                disabled={sigSaving || sigEmpty || !selectedLawyerId}
                className="mt-3 brand-gradient-bg text-white"
                data-testid="vollmacht-sign-save-btn"
                title={!selectedLawyerId ? "Bitte zuerst eine Kanzlei auswählen" : ""}
              >
                {sigSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PenLine className="h-4 w-4 mr-2" />}
                Vollmacht unterschreiben
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
